'use client';

import { useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

export type ChatVariant =
  | 'imessage-chat-generator'
  | 'whatsapp-chat-generator'
  | 'telegram-chat-generator'
  | 'discord-message-generator'
  | 'instagram-dm-generator';

const theme: Record<
  ChatVariant,
  {
    badge: string;
    title: string;
    bg: string;
    me: string;
    meText: string;
    them: string;
    themText: string;
    // Discord does not draw bubbles: a channel is a flat list of avatar + coloured
    // username + message, on the dark theme. Same input, different renderer.
    layout?: 'bubbles' | 'feed';
    meName?: string;
    themName?: string;
    meNameColor?: string;
    themNameColor?: string;
    // The chat header is what makes a mockup recognisable as the platform. Without it the
    // render is generic grey-and-blue bubbles: instagram-dm-generator bounced at 61% with
    // 12 seconds per session while its sibling instagram-story-generator, which draws the
    // full Instagram chrome, bounced at 3.7%.
    header: {
      bg: string;
      text: string;
      sub: string;
      avatar: string;
      subtitle: string;
      // Instagram rings the avatar in its gradient; the others use a flat circle.
      ring?: boolean;
    };
    // iMessage only. Checked four incumbents (10015.io, thefake.design, commoninja,
    // brainrotshorts) on 2026-09-19: all four already draw a phone status bar, and
    // thefake.design/commoninja both place "Delivered" correctly (only under the most
    // recent outgoing bubble, never every one) plus a date/time divider above the thread.
    // There was no feature gap left to be first at, the way the roman-numeral converter's
    // extended range was ahead of every competitor. This closes OUR gap to their baseline
    // instead: without it the render was missing every detail that makes a screenshot read
    // as real, which is the same mechanism that fixed whatsapp-chat-generator's bounce rate.
    iosChrome?: boolean;
  }
> = {
  'imessage-chat-generator': {
    badge: 'iMessage',
    title: 'iMessage chat generator',
    header: {
      bg: '#f6f6f6',
      text: '#000000',
      sub: '#8e8e93',
      avatar: '#c7c7cc',
      subtitle: 'iMessage',
    },
    bg: '#ffffff',
    me: '#0b93f6',
    meText: '#ffffff',
    them: '#e5e5ea',
    themText: '#000000',
    iosChrome: true,
  },
  'whatsapp-chat-generator': {
    badge: 'WhatsApp',
    title: 'WhatsApp chat generator',
    header: {
      bg: '#075e54',
      text: '#ffffff',
      sub: '#cfe9e5',
      avatar: '#25d366',
      subtitle: 'online',
    },
    bg: '#e5ddd5',
    me: '#dcf8c6',
    meText: '#000000',
    them: '#ffffff',
    themText: '#000000',
  },
  'telegram-chat-generator': {
    badge: 'Telegram',
    title: 'Telegram chat generator',
    header: {
      bg: '#527da3',
      text: '#ffffff',
      sub: '#d3e2f0',
      avatar: '#8ab4dd',
      subtitle: 'online',
    },
    bg: '#e7ebf0',
    me: '#eeffde',
    meText: '#000000',
    them: '#ffffff',
    themText: '#000000',
  },
  'discord-message-generator': {
    badge: 'Discord',
    title: 'Discord message generator',
    header: {
      bg: '#2b2d31',
      text: '#f2f3f5',
      sub: '#949ba4',
      avatar: '#5865f2',
      subtitle: 'text channel',
    },
    bg: '#313338',
    me: '#313338',
    meText: '#dbdee1',
    them: '#313338',
    themText: '#dbdee1',
    layout: 'feed',
    meName: 'you',
    themName: 'friend',
    meNameColor: '#5865f2',
    themNameColor: '#f23f43',
  },
  'instagram-dm-generator': {
    badge: 'Instagram',
    title: 'Instagram DM generator',
    header: {
      bg: '#ffffff',
      text: '#000000',
      sub: '#8e8e8e',
      avatar: '#dd2a7b',
      subtitle: 'Active now',
      ring: true,
    },
    bg: '#ffffff',
    me: '#3797f0',
    meText: '#ffffff',
    them: '#efefef',
    themText: '#000000',
  },
};

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  const words = text.split(' ');
  let current = '';
  for (const word of words) {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const HEADER_H = 64;
const STATUS_BAR_H = 28;
const DATE_LABEL_H = 30;

// The iOS status bar above the contact header. Drawn with simple shapes rather than a real
// icon font: the shapes read as "signal / wifi / battery" at this size, and it is the first
// thing a viewer's eye checks to decide whether a screenshot is a real phone or a graphic.
function drawStatusBar(ctx: CanvasRenderingContext2D, width: number, time: string) {
  ctx.fillStyle = '#f6f6f6';
  ctx.fillRect(0, 0, width, STATUS_BAR_H);

  ctx.fillStyle = '#000000';
  ctx.font = '600 14px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  ctx.fillText(time.trim() || '9:41', 18, STATUS_BAR_H / 2 + 1);

  // Signal: four ascending bars.
  const barsX = width - 90;
  const barW = 3;
  const gap = 2;
  for (let i = 0; i < 4; i += 1) {
    const h = 4 + i * 2.5;
    ctx.fillRect(barsX + i * (barW + gap), STATUS_BAR_H / 2 + 5 - h, barW, h);
  }

  // Wifi: two concentric arcs plus a dot, the recognisable fan shape.
  const wifiX = width - 64;
  const wifiY = STATUS_BAR_H / 2 + 4;
  ctx.beginPath();
  ctx.arc(wifiX, wifiY, 6, Math.PI * 1.25, Math.PI * 1.75);
  ctx.lineWidth = 1.6;
  ctx.strokeStyle = '#000000';
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(wifiX, wifiY, 3, Math.PI * 1.25, Math.PI * 1.75);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(wifiX, wifiY + 0.5, 1.1, 0, Math.PI * 2);
  ctx.fillStyle = '#000000';
  ctx.fill();

  // Battery: a rounded outline with a small terminal nub and a filled body.
  const battX = width - 46;
  const battY = STATUS_BAR_H / 2 - 6;
  roundRect(ctx, battX, battY, 22, 12, 3);
  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.fillStyle = '#000000';
  ctx.fillRect(battX + 24, battY + 3.5, 1.6, 5);
  ctx.fillStyle = '#000000';
  roundRect(ctx, battX + 1.5, battY + 1.5, 17, 9, 2);
  ctx.fill();

  ctx.textBaseline = 'alphabetic';
}

// Real iMessage shows one small "Today HH:MM" label above the first bubble a viewer scrolls
// to, not a timestamp under every line the way a naive mockup generator lets you type one in
// anywhere. One label, computed once, is both more accurate and simpler to read.
function drawDateLabel(ctx: CanvasRenderingContext2D, width: number, y: number, time: string) {
  ctx.fillStyle = '#8e8e93';
  ctx.font = '600 12px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`Today ${time.trim() || '9:41'}`, width / 2, y);
  ctx.textAlign = 'left';
}

// Apple shows "Delivered" under the most recently sent bubble only, and it disappears the
// moment another message follows it. Printing it under every outgoing bubble, the way several
// competing generators do, is the detail that gives a mockup away fastest.
function drawDeliveredLabel(ctx: CanvasRenderingContext2D, width: number, y: number) {
  ctx.fillStyle = '#8e8e93';
  ctx.font = '400 12px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText('Delivered', width - 16, y);
  ctx.textAlign = 'left';
}

// Draws the platform chrome at the top of the render. This is the difference between a
// mockup someone recognises and generic bubbles: see the note on the `header` theme field.
function drawHeader(
  ctx: CanvasRenderingContext2D,
  t: (typeof theme)[ChatVariant],
  width: number,
  name: string,
) {
  const h = t.header;
  ctx.fillStyle = h.bg;
  ctx.fillRect(0, 0, width, HEADER_H);

  // Hairline under the header, the way every one of these apps separates it from the thread.
  ctx.fillStyle = 'rgba(0,0,0,0.12)';
  ctx.fillRect(0, HEADER_H - 1, width, 1);

  const cx = 16 + 20;
  const cy = HEADER_H / 2;

  if (h.ring) {
    // Instagram's story ring: a warm gradient around the avatar.
    const grad = ctx.createLinearGradient(cx - 22, cy - 22, cx + 22, cy + 22);
    grad.addColorStop(0, '#f9ce34');
    grad.addColorStop(0.5, '#ee2a7b');
    grad.addColorStop(1, '#6228d7');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = h.bg;
    ctx.beginPath();
    ctx.arc(cx, cy, 19, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.fillStyle = h.avatar;
  ctx.beginPath();
  ctx.arc(cx, cy, h.ring ? 17 : 20, 0, Math.PI * 2);
  ctx.fill();

  // Initial, so the avatar reads as a person rather than a coloured dot.
  const initial = (name.trim()[0] || '?').toUpperCase();
  ctx.fillStyle = '#ffffff';
  ctx.font = '600 18px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initial, cx, cy + 1);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';

  const textX = cx + 20 + 14;
  ctx.fillStyle = h.text;
  ctx.font = '600 17px system-ui, sans-serif';
  ctx.fillText(name.trim() || 'Contact', textX, cy - 2);
  ctx.fillStyle = h.sub;
  ctx.font = '400 13px system-ui, sans-serif';
  ctx.fillText(h.subtitle, textX, cy + 16);
}

export default function BrowserChatGenerator({ variant }: { variant: ChatVariant }) {
  const t = theme[variant];
  const [messages, setMessages] = useState(
    'Hey! Did you try that new tool?\n> Yes, it runs entirely in the browser\nNo way, that is private and fast\n> Exactly, nothing gets uploaded',
  );
  const [contact, setContact] = useState(
    variant === 'discord-message-generator' ? 'general' : 'Alex',
  );
  const [time, setTime] = useState('9:41');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const scale = 2;
    const width = 480;
    const pad = 16;
    const maxBubble = width * 0.7;
    const font = '400 18px system-ui, sans-serif';
    const lineHeight = 24;

    const parsed = messages
      .split('\n')
      .map((line) => line.trimEnd())
      .filter((line) => line.trim() !== '')
      .map((line) => {
        const me = line.startsWith('>');
        return { me, text: me ? line.slice(1).trim() : line.trim() };
      });

    ctx.font = font;

    if (t.layout === 'feed') {
      // Discord-style channel: avatar circle, coloured username, message underneath.
      const avatar = 40;
      const textX = pad + avatar + 12;
      const textWidth = width - textX - pad;
      const feed = parsed.map((m) => {
        const lines = wrap(ctx, m.text, textWidth);
        return { ...m, lines, h: 22 + lines.length * lineHeight };
      });

      const totalHeight = HEADER_H + pad + feed.reduce((sum, m) => sum + m.h + 12, 0) + pad;
      canvas.width = width * scale;
      canvas.height = totalHeight * scale;
      ctx.scale(scale, scale);
      ctx.fillStyle = t.bg;
      ctx.fillRect(0, 0, width, totalHeight);
      drawHeader(ctx, t, width, contact);

      let fy = HEADER_H + pad;
      for (const m of feed) {
        ctx.fillStyle = m.me ? (t.meNameColor ?? '#5865f2') : (t.themNameColor ?? '#f23f43');
        ctx.beginPath();
        ctx.arc(pad + avatar / 2, fy + avatar / 2, avatar / 2, 0, Math.PI * 2);
        ctx.fill();

        ctx.font = '600 17px system-ui, sans-serif';
        ctx.fillText(
          (m.me ? (t.meName ?? 'you') : (t.themName ?? 'friend')) as string,
          textX,
          fy + 16,
        );

        ctx.font = font;
        ctx.fillStyle = m.me ? t.meText : t.themText;
        let ty = fy + 16 + lineHeight;
        for (const line of m.lines) {
          ctx.fillText(line, textX, ty);
          ty += lineHeight;
        }
        fy += Math.max(m.h, avatar) + 12;
      }

      canvas.toBlob((blob) => {
        if (blob) setDownloadUrl(URL.createObjectURL(blob));
      }, 'image/png');
      return;
    }

    const layout = parsed.map((m) => {
      const lines = wrap(ctx, m.text, maxBubble - 28);
      const w = Math.min(maxBubble, Math.max(...lines.map((l) => ctx.measureText(l).width)) + 28);
      const h = lines.length * lineHeight + 16;
      return { ...m, lines, w, h };
    });

    const chromeH = t.iosChrome ? STATUS_BAR_H : 0;
    const dateH = t.iosChrome && layout.length ? DATE_LABEL_H : 0;
    const lastIsMine = layout.length > 0 && layout[layout.length - 1].me;
    const deliveredH = t.iosChrome && lastIsMine ? 20 : 0;
    const totalHeight =
      chromeH +
      HEADER_H +
      dateH +
      pad +
      layout.reduce((sum, m) => sum + m.h + 10, 0) +
      deliveredH +
      pad;
    canvas.width = width * scale;
    canvas.height = totalHeight * scale;
    ctx.scale(scale, scale);

    ctx.fillStyle = t.bg;
    ctx.fillRect(0, 0, width, totalHeight);
    if (t.iosChrome) {
      drawStatusBar(ctx, width, time);
    }
    ctx.translate(0, chromeH);
    drawHeader(ctx, t, width, contact);
    if (dateH) {
      drawDateLabel(ctx, width, HEADER_H + dateH - 10, time);
    }

    let y = HEADER_H + dateH + pad;
    ctx.font = font;
    for (const m of layout) {
      const x = m.me ? width - pad - m.w : pad;
      ctx.fillStyle = m.me ? t.me : t.them;
      roundRect(ctx, x, y, m.w, m.h, 16);
      ctx.fill();
      ctx.fillStyle = m.me ? t.meText : t.themText;
      let ty = y + 24;
      for (const line of m.lines) {
        ctx.fillText(line, x + 14, ty);
        ty += lineHeight;
      }
      y += m.h + 10;
    }
    if (deliveredH) {
      drawDeliveredLabel(ctx, width, y + 4);
    }
    ctx.translate(0, -chromeH);

    canvas.toBlob((blob) => {
      if (blob) setDownloadUrl(URL.createObjectURL(blob));
    }, 'image/png');
  }, [messages, t, contact, time]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{t.badge}</span>
        <h2 className="tool-live-title">{t.title}</h2>
        <p className="tool-live-subtitle">
          Write a mock conversation and download it as an image. Rendered entirely in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>{variant === 'discord-message-generator' ? 'Channel name' : 'Contact name'}</span>
            <input
              maxLength={32}
              onChange={(e) => setContact(e.target.value)}
              type="text"
              value={contact}
            />
          </label>

          {t.iosChrome ? (
            <label className="tool-live-field">
              <span>Time</span>
              <input
                maxLength={8}
                onChange={(e) => setTime(e.target.value)}
                placeholder="9:41"
                type="text"
                value={time}
              />
            </label>
          ) : null}

          <label className="tool-live-field tool-live-field-full">
            <span>Messages (start a line with &gt; for your own message)</span>
            <textarea onChange={(e) => setMessages(e.target.value)} rows={8} value={messages} />
          </label>
          <div className="tool-live-actions">
            {downloadUrl ? (
              <a
                className="button button-secondary"
                download={`${variant}.png`}
                href={downloadUrl}
                // Fire-and-forget: this tool renders entirely client-side and the file is
                // already in memory, so the download must never wait on or be blocked by this.
                // It exists only so tool_used fires on the actual action (a download), not on
                // every keystroke that redraws the canvas. Without any call here this whole
                // five-variant family reported zero usage despite real GSC clicks in the
                // hundreds, found 2026-09-20.
                onClick={() => {
                  void consumeToolUsage(variant, '').catch(() => {});
                }}
              >
                Download PNG
              </a>
            ) : null}
          </div>
        </section>

        <section className="tool-live-preview">
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
            }}
          />
          <p className="tool-live-panel-note">
            Rendered locally with an HTML canvas. This is a mockup for design and fun.
          </p>
        </section>
      </div>
    </div>
  );
}
